import { ArrowRightIcon, ExternalLinkIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { FormRadioGroupDS } from 'components/reusable/form/FormFields'
import { Banner, Button, ButtonGroup, TextLink, useBreakpoint } from 'slab'
import {
  conditionallyLocalizeDate,
  fullStateNameFromAbbreviation,
} from 'utils'
import { type MemberOnboardingEnrollmentEvent } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { clearOnboardingStorage } from '../navigationUtils'
import { useOnboarding } from '../OnboardingContext'
import { type DeclineReason, type StepComponentProps } from '../types'
import { isEnrollmentEventQle, isRenewal, isRenewalOrQleFlow } from '../utils'

type Props = {
  subsidy?: boolean;
  offerDental?: boolean | null;
  offerVision?: boolean | null;
  navigateToPreviousStep?: StepComponentProps['navigateToPreviousStep'];
}

function getFormattedPlans (
  offerDental?: boolean | null,
  offerVision?: boolean | null
): string {
  const plans = ['medical']
  if (offerDental) plans.push('dental')
  if (offerVision) plans.push('vision')

  if (plans.length === 1) return plans[0]
  if (plans.length === 2) return `${plans[0]} and ${plans[1]}`
  return `${plans.slice(0, -1).join(', ')}, or ${plans[plans.length - 1]}`
}

function getInnerContent ({
  enrollmentEvent,
  formattedPlans,
  hasMultiplePlans,
  declinedWithSubsidy,
  isThisEnrollmentEventRenewal,
  isThisEnrollmentEventQle,
}: {
  enrollmentEvent: MemberOnboardingEnrollmentEvent;
  formattedPlans: string;
  hasMultiplePlans?: boolean | null;
  declinedWithSubsidy?: boolean | null;
  isThisEnrollmentEventRenewal?: boolean | null;
  isThisEnrollmentEventQle?: boolean | null;
}) {
  if (declinedWithSubsidy) {
    return (
      <p>
        You've declined your company's health insurance allowance offer and will
        not be enrolled in any {formattedPlans} plans. There are no additional
        steps to take at this time but if you have any questions, send us an
        email at{' '}
        <TextLink color='purple' href='mailto:support@thatch.com'>
          support@thatch.com
        </TextLink>
        .
      </p>
    )
  }

  if (isThisEnrollmentEventRenewal) {
    const planYear = enrollmentEvent.plan_year
    const enrollmentWindowEndDate = enrollmentEvent.end_date
    const localizedWindowEndDate = conditionallyLocalizeDate(
      new Date(enrollmentWindowEndDate)
    )
    return (
      <>
        <p>
          That means you won't be enrolled in any {formattedPlans} plans through
          us in {planYear}.
        </p>
        <p>
          It also means that you'll need to either cancel your {planYear - 1}{' '}
          plan{hasMultiplePlans ? 's' : ''} or update the payment method on{' '}
          {hasMultiplePlans ? 'them' : 'it'} to your own card or bank account.
          Your member dashboard will have any other steps needed from you.
        </p>
        <p>
          If you change your mind, you have until {localizedWindowEndDate} to
          reach out to
          <TextLink color='purple' href='mailto:support@thatch.com'>
            support@thatch.com
          </TextLink>{' '}
          to reopen your window. Otherwise, your next opportunity to enroll will
          be during open enrollment next year.
        </p>
      </>
    )
  }

  if (isThisEnrollmentEventQle) {
    const planYear = enrollmentEvent.plan_year
    const enrollmentWindowEndDate = enrollmentEvent.end_date
    const localizedWindowEndDate = conditionallyLocalizeDate(
      new Date(enrollmentWindowEndDate)
    )
    const nextPlanYear = planYear + 1

    // Calculate last day of current month
    const now = new Date()
    const lastDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    const localizedLastDayOfCurrentMonth = conditionallyLocalizeDate(lastDayOfCurrentMonth)

    return (
      <>
        <p>
          That means Thatch will stop managing and paying for your medical, dental, and vision plans on {localizedLastDayOfCurrentMonth}.
        </p>
        <p>
          <b className='font-semibold'>You must cancel your plans or put down your own payment method for them.</b> Your member dashboard will let you know if any other steps are needed from you.
        </p>
        <p>
          If you change your mind, you can reach out to{' '}
          <TextLink color='purple' href='mailto:support@thatch.com'>
            support@thatch.com
          </TextLink>{' '}
          before {localizedWindowEndDate}. Otherwise, your next chance to enroll will be in November for coverage in {nextPlanYear}.
        </p>
      </>
    )
  }

  return (
    <>
      <p>
        That means you won't be enrolled in any {formattedPlans} plans through
        us.
      </p>
      <p>
        If you change your mind and your enrollment window is still open, you
        can reach out to{' '}
        <TextLink color='purple' href='mailto:support@thatch.com'>
          support@thatch.com
        </TextLink>
        . Otherwise, your next opportunity to enroll will be during the next
        open enrollment.
      </p>
    </>
  )
}

function OptedOutFinalizedCmp ({
  offerDental,
  offerVision,
  declinedWithSubsidy,
}: Props & {
  declinedWithSubsidy?: boolean;
}) {
  const { assign } = useWindowLocation()
  const { onboardingData, enrollmentEventId, newQleEnabled } = useOnboarding()
  const { breakpointIsAtLeast } = useBreakpoint()
  const formattedPlans = getFormattedPlans(offerDental, offerVision)
  const hasMultiplePlans = offerDental || offerVision
  const exchangeLink = onboardingData?.restrictions?.subsidy_eligible
  const memberState = onboardingData?.member?.state

  const handleGoBackToDashboard = () => {
    assign('/')
  }

  const isThisEnrollmentEventRenewal = onboardingData
    ? isRenewal(onboardingData)
    : undefined

  const isThisEnrollmentEventQle = onboardingData
    ? isEnrollmentEventQle(onboardingData.enrollment_event)
    : undefined

  const isUsingRenewalFlow = onboardingData && isRenewalOrQleFlow(onboardingData, newQleEnabled)

  const handleLearnMore = () => {
    window.open(exchangeLink || 'https://healthcare.gov/', '_blank')
  }

  useEffect(() => {
    // since this is a terminal step of the onboarding, we clear localStorage
    // on any visit history so that the user can start from the beginning
    // if they choose to start the onboarding process again
    if (enrollmentEventId) {
      clearOnboardingStorage(enrollmentEventId)
    }
  }, [enrollmentEventId])

  function getExchangeButtonText () {
    const stateName =
      exchangeLink !== 'https://healthcare.gov/' && !!memberState
        ? fullStateNameFromAbbreviation(memberState)
        : ''

    if (breakpointIsAtLeast('xs')) {
      return stateName
        ? `Visit the ${stateName} health insurance exchange`
        : 'Visit the health insurance exchange'
    }

    return stateName ? `Visit ${stateName} exchange` : 'Visit exchange'
  }

  function renderButtons () {
    const showButtons = isUsingRenewalFlow || declinedWithSubsidy

    if (showButtons) {
      const goBackToDashboardButton = isUsingRenewalFlow && (
        <Button
          variant={declinedWithSubsidy ? 'secondary' : 'primary'}
          onClick={handleGoBackToDashboard}
          rightIcon={!declinedWithSubsidy ? ArrowRightIcon : undefined}
        >
          Go to dashboard
        </Button>
      )

      const goToExchangeButton = declinedWithSubsidy && (
        <Button
          rightIcon={ExternalLinkIcon}
          variant='primary'
          onClick={handleLearnMore}
        >
          {getExchangeButtonText()}
        </Button>
      )

      if (breakpointIsAtLeast('xs')) {
        return (
          <div className='pt-4 px-6 border-t border-gray-200'>
            <ButtonGroup
              size='large'
              align={declinedWithSubsidy ? 'center' : 'right'}
              layout={declinedWithSubsidy ? 'vertical' : 'horizontal'}
            >
              {goBackToDashboardButton}
              {goToExchangeButton}
            </ButtonGroup>
          </div>
        )
      }

      return (
        <div className='pt-4 px-6 border-t border-gray-200'>
          <ButtonGroup size='large' layout='vertical'>
            {goToExchangeButton}
            {goBackToDashboardButton}
          </ButtonGroup>
        </div>
      )
    }
  }

  return (
    <>
      <h2 className='heading-md mb-6 px-6 text-gray-800'>
        {isThisEnrollmentEventQle ? "You've ended your healthcare benefits" : "You've declined your healthcare offer"}
      </h2>
      <div className='space-y-3 body text-gray-800 mb-6 px-6'>
        {onboardingData?.enrollment_event &&
          getInnerContent({
            enrollmentEvent: onboardingData.enrollment_event,
            formattedPlans,
            hasMultiplePlans,
            declinedWithSubsidy,
            isThisEnrollmentEventRenewal,
            isThisEnrollmentEventQle,
          })}
      </div>
      {renderButtons()}
    </>
  )
}

type DeclineReasonForm = {
  decline_reason: DeclineReason;
}

function OptOutCmp ({
  offerDental,
  offerVision,
  navigateToPreviousStep,
  onSubmit,
  submitError,
  isPending,
  isFromSubsidyEligible,
  planSelectionSupportCallSchedulingUrl,
}: Props & {
  onSubmit: (formData: DeclineReasonForm) => void;
  submitError: React.ReactElement | null;
  isPending: boolean;
  isFromSubsidyEligible: boolean;
  planSelectionSupportCallSchedulingUrl?: string | null;
}) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const { onboardingData } = useOnboarding()
  const formattedPlans = getFormattedPlans(offerDental, offerVision)
  const planYear = onboardingData?.enrollment_event?.plan_year
  const hasMultiplePlans = offerDental || offerVision
  const { control, handleSubmit } = useForm<DeclineReasonForm>({
    defaultValues: {
      decline_reason: isFromSubsidyEligible ? 'subsidy' : undefined,
    },
  })

  const isThisEnrollmentEventQle = onboardingData
    ? isEnrollmentEventQle(onboardingData.enrollment_event)
    : undefined

  function renderButtons () {
    const declineButton = (
      <Button size='large' variant='tertiary-destructive' type='submit'>
        Yes, I want to decline coverage
      </Button>
    )

    const backButton = (
      <Button
        size='large'
        variant='secondary'
        onClick={() => navigateToPreviousStep?.()}
      >
        Never mind, go back
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <ButtonGroup size='large'>
          {declineButton}
          {backButton}
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup size='large' layout='vertical'>
        {backButton}
        {declineButton}
      </ButtonGroup>
    )
  }

  return (
    <>
      <h2 className='heading-md mb-6 px-6 text-gray-800'>
        {isFromSubsidyEligible
          ? "Confirm you'll be using your subsidy"
          : 'Are you sure you want to decline?'}
      </h2>
      <div className='space-y-3 body text-gray-800 mb-6 px-6'>
        {isThisEnrollmentEventQle
          ? (
            <>
              <p>
                Declining means Thatch will no longer manage your coverage in {formattedPlans} health insurance
                {planYear ? ` for ${planYear}` : ''}, and you'll lose the allowance your employer is
                offering to help cover your healthcare costs.
              </p>
              <p>
                <b className='font-semibold'>You'll be responsible for either cancelling your plan{hasMultiplePlans ? 's' : ''} or taking over payment for them.</b>
              </p>
            </>
            )
          : (
            <p>
              Declining means you <b className='font-semibold'>won't be enrolled</b>{' '}
              in {formattedPlans} health insurance
              {planYear ? ` for ${planYear}` : ''}, and you'll{' '}
              <b className='font-semibold'>lose the allowance</b> your employer is
              offering to help cover your healthcare costs.
            </p>
            )}
        {isFromSubsidyEligible && (
          <p>
            Accepting the subsidy means you'll have to find a plan on your
            state's health insurance exchange website.
          </p>
        )}
        {!isFromSubsidyEligible && planSelectionSupportCallSchedulingUrl && (
          <p>
            If you'd like to get help finding a plan that fits your needs, you
            can{' '}
            <TextLink
              color='purple'
              href={planSelectionSupportCallSchedulingUrl}
              target='_blank'
            >
              speak with one of our licensed agents
            </TextLink>
            .
          </p>
        )}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        {!isFromSubsidyEligible && (
          <div className='pb-6 px-6'>
            <FormRadioGroupDS
              control={control}
              name='decline_reason'
              label='Decline reason:'
              rules={{ required: 'Please select a reason for declining' }}
              options={[
                {
                  label: "Already covered by a parent's or spouse's plan",
                  value: 'covered_by_family',
                },
                {
                  label:
                    'Have a plan with a premium tax credit, or planning to get one',
                  value: 'subsidy',
                },
                {
                  label: "Didn't like the plans I saw",
                  value: 'dislike_plans',
                },
                {
                  label:
                    "Budget wasn't enough to make the plans I saw affordable",
                  value: 'budget_not_affordable',
                },
                {
                  label: "Don't need health insurance",
                  value: 'insurance_not_needed',
                },
                {
                  label: 'Other',
                  value: 'other',
                },
              ]}
              disabled={isPending}
            />
          </div>
        )}
        {submitError && (
          <div className='mb-6 px-6'>
            <Banner
              title='A problem has occurred while processing your request'
              type='critical'
            >
              {submitError}
            </Banner>
          </div>
        )}
        <div className='pt-4 px-6 border-t border-gray-200'>
          {renderButtons()}
        </div>
      </form>
    </>
  )
}

export function OptedOutPage ({
  navigateToPreviousStep,
  isFromSubsidyEligible = false,
}: StepComponentProps & { isFromSubsidyEligible?: boolean }) {
  const { onboardingData, updateMemberInfo, refreshOnboardingInfo } =
    useOnboarding()
  const [optedOut, setOptedOut] = useState(false)
  const [submitError, setSubmitError] = useState<React.ReactElement | null>(
    null
  )
  const [declinedWithSubsidy, setDeclinedWithSubsidy] = useState(false)

  useEffect(() => {
    const isOptedOut = onboardingData?.progress === 'opted_out'
    setOptedOut(isOptedOut)
    if (isOptedOut) {
      setDeclinedWithSubsidy(onboardingData?.decline_reason === 'subsidy')
    }
  }, [onboardingData?.progress, onboardingData?.decline_reason])

  const { mutate, isPending } = updateMemberInfo

  const onHandleError = (error: GrapeAPIErrorType) => {
    const errorMessage = <OnboardingGenericErrorMessage error={error} />
    setSubmitError(errorMessage)
  }

  const handleDeclineCoverage = (formData: DeclineReasonForm) => {
    mutate(formData, {
      onSuccess: () => {
        refreshOnboardingInfo()
        setOptedOut(true)
        setDeclinedWithSubsidy(formData.decline_reason === 'subsidy')
      },
      onError: (error) => {
        onHandleError(error)
      },
    })
  }

  const sharedProps = {
    offerDental: !onboardingData?.restrictions?.dental_not_available,
    offerVision: !onboardingData?.restrictions?.vision_not_available,
  }

  return (
    <div
      data-testid='OptedOutPage'
      className='w-screen min-h-svh xs:min-h-screen bg-gray-50 flex items-center justify-center p-4'
    >
      <div className='bg-white border rounded-lg shadow-md pt-6 pb-4 max-w-lg w-full'>
        {optedOut
          ? (
            <OptedOutFinalizedCmp
              declinedWithSubsidy={declinedWithSubsidy}
              {...sharedProps}
            />
            )
          : (
            <OptOutCmp
              {...sharedProps}
              isPending={isPending}
              onSubmit={handleDeclineCoverage}
              submitError={submitError}
              navigateToPreviousStep={navigateToPreviousStep}
              isFromSubsidyEligible={isFromSubsidyEligible}
              planSelectionSupportCallSchedulingUrl={
              onboardingData?.member?.plan_selection_call_scheduling_links?.en
            }
            />
            )}
      </div>
    </div>
  )
}

export function OptedOutFromSubsidyEligiblePage (props: StepComponentProps) {
  return <OptedOutPage {...props} isFromSubsidyEligible />
}
